import { useEffect } from 'react';

/**
 * Prevents iOS rubber-band / pull-to-refresh effect from propagating outside the given element
 * while still allowing the element itself to scroll.
 *
 * @param {React.RefObject<HTMLElement>} ref - element that should capture scroll gestures
 */
const usePreventScrollBounce = ref => {
  useEffect(() => {
    const el = ref?.current;
    if (!el) {
      return undefined;
    }

    let lastY = 0;

    const handleTouchStart = event => {
      if (event.touches.length !== 1) {
        return;
      }
      lastY = event.touches[0].clientY;
    };

    const handleTouchMove = event => {
      if (event.touches.length !== 1) {
        return;
      }

      const currentY = event.touches[0].clientY;
      const deltaY = currentY - lastY;
      lastY = currentY;

      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const isScrollable = scrollHeight > clientHeight + 1;

      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const pullingDown = deltaY > 0;
      const pullingUp = deltaY < 0;

      const shouldPrevent =
        !isScrollable ||
        (atTop && pullingDown) ||
        (atBottom && pullingUp);

      if (shouldPrevent && event.cancelable) {
        event.preventDefault();
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [ref]);
};

export default usePreventScrollBounce;
